import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  alpha,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  RocketLaunch as RocketIcon,
  Login as LoginIcon,
  Email as EmailIcon,
  AttachFile as AttachIcon,
  Folder as FolderIcon,
  Security as SecurityIcon,
  Dns as DnsIcon,
  Group as GroupIcon,
  Settings as SettingsIcon,
  PhoneAndroid as MobileIcon,
  ContactMail as ContactIcon,
  CalendarMonth as CalendarIcon,
  Task as TaskIcon,
  AutoAwesome as AIIcon,
  Search as SearchIcon,
  Keyboard as KeyboardIcon,
  CheckCircleOutline as CheckIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { useBranding } from '../../utils/BrandingContext';

interface GuideSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  badge?: string;
  content: React.ReactNode;
}

const GuidePage: React.FC = () => {
  const theme = useTheme();
  const { branding } = useBranding();
  const [expanded, setExpanded] = useState<string | false>('getting-started');
  const isDark = theme.palette.mode === 'dark';

  const handleChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const StepItem: React.FC<{ step: number; text: string }> = ({ step, text }) => (
    <ListItem sx={{ py: 0.5, pl: 0 }}>
      <ListItemIcon sx={{ minWidth: 36 }}>
        <Box
          sx={{
            width: 24, height: 24, borderRadius: '50%',
            bgcolor: theme.palette.primary.main, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.75rem', fontWeight: 700,
          }}
        >
          {step}
        </Box>
      </ListItemIcon>
      <ListItemText primary={text} primaryTypographyProps={{ fontSize: '0.9rem' }} />
    </ListItem>
  );

  const TipBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Box
      sx={{
        mt: 1.5, p: 1.5, borderRadius: 1.5,
        bgcolor: alpha(theme.palette.info.main, 0.08),
        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
      }}
    >
      <Typography variant="body2" sx={{ fontSize: '0.85rem', color: theme.palette.text.secondary }}>
        💡 <strong>Tip:</strong> {children}
      </Typography>
    </Box>
  );

  const sections: GuideSection[] = [
    {
      id: 'getting-started',
      title: 'Getting Started with CruvzMail',
      icon: <RocketIcon />,
      badge: 'Start Here',
      content: (
        <Box>
          <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.7 }}>
            Welcome to {branding.companyName || 'CruvzMail'} — your secure, self-hosted email platform built for
            professionals and organizations. This guide walks you through everything from your first
            login to mastering advanced features.
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>What you can do:</Typography>
          <List dense disablePadding>
            {[
              'Send and receive emails with rich formatting & attachments',
              'Organize messages with folders, labels, and smart filters',
              'Manage contacts, calendars, tasks, and notes in one place',
              'Use AI-powered tools for drafting and summarizing emails',
              'Track email delivery status in real-time',
              'Customize your experience with themes, signatures, and settings',
            ].map((item, i) => (
              <ListItem key={i} sx={{ py: 0.25, pl: 0 }}>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <CheckIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
                </ListItemIcon>
                <ListItemText primary={item} primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItem>
            ))}
          </List>
          <TipBox>
            Bookmark this page for quick reference as you explore the platform.
          </TipBox>
        </Box>
      ),
    },
    {
      id: 'signing-in',
      title: 'Signing In to Your Account',
      icon: <LoginIcon />,
      content: (
        <Box>
          <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.7 }}>
            Access your mailbox by navigating to your organization's CruvzMail URL. Your administrator
            will provide your login credentials during onboarding.
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>How to sign in:</Typography>
          <List dense disablePadding>
            <StepItem step={1} text="Open your browser and go to your CruvzMail URL (e.g., mail.yourdomain.com)" />
            <StepItem step={2} text="Enter your full email address in the username field" />
            <StepItem step={3} text="Type your password and click Sign In" />
            <StepItem step={4} text="You'll land on your Inbox — ready to go!" />
          </List>
          <TipBox>
            If you forget your password, use the &quot;Forgot Password&quot; link on the login page.
            Your admin can also reset it from the admin panel.
          </TipBox>
        </Box>
      ),
    },
    {
      id: 'email-hosting',
      title: 'Understanding Email Hosting & Domains',
      icon: <DnsIcon />,
      content: (
        <Box>
          <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.7 }}>
            CruvzMail operates on your own domain, giving your organization full control over email
            infrastructure. Your emails are stored on secure servers managed by your team — not a
            third-party cloud provider.
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Key concepts:</Typography>
          <List dense disablePadding>
            {[
              { label: 'Custom Domain', desc: 'Use your business domain (e.g., you@company.com) for a professional identity' },
              { label: 'DNS Records', desc: 'MX, SPF, DKIM, and DMARC records ensure your emails are delivered and trusted' },
              { label: 'Multi-Domain', desc: 'Organizations can host multiple domains under a single CruvzMail instance' },
              { label: 'Self-Hosted', desc: 'Your data stays on your servers — full privacy and compliance control' },
            ].map((item, i) => (
              <ListItem key={i} sx={{ py: 0.5, pl: 0, alignItems: 'flex-start' }}>
                <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}>
                  <ArrowIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  secondary={item.desc}
                  primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600 }}
                  secondaryTypographyProps={{ fontSize: '0.825rem' }}
                />
              </ListItem>
            ))}
          </List>
          <TipBox>
            Ask your domain administrator to verify that SPF, DKIM, and DMARC records are properly
            configured for optimal deliverability.
          </TipBox>
        </Box>
      ),
    },
    {
      id: 'composing-emails',
      title: 'Composing & Sending Emails',
      icon: <EmailIcon />,
      content: (
        <Box>
          <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.7 }}>
            CruvzMail features a powerful compose editor with rich text formatting, inline images,
            and multi-tab support — so you can work on several drafts at once.
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Composing a new email:</Typography>
          <List dense disablePadding>
            <StepItem step={1} text="Click the Compose button in the sidebar or press 'C' on your keyboard" />
            <StepItem step={2} text="Add recipients in the To field — use Cc and Bcc for additional recipients" />
            <StepItem step={3} text="Write your subject line and compose your message" />
            <StepItem step={4} text="Use the toolbar to format text, add links, insert images, or change fonts" />
            <StepItem step={5} text="Click Send when ready, or close the tab to save as a draft" />
          </List>
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>Actions on received emails:</Typography>
          <List dense disablePadding>
            {[
              'Reply — Respond directly to the sender',
              'Reply All — Respond to the sender and all recipients',
              'Forward — Send the email to someone else',
              'Edit as New — Re-compose the email with all original content pre-filled',
            ].map((item, i) => (
              <ListItem key={i} sx={{ py: 0.25, pl: 0 }}>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <CheckIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
                </ListItemIcon>
                <ListItemText primary={item} primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItem>
            ))}
          </List>
          <TipBox>
            You can open multiple compose tabs simultaneously. Drafts auto-save so you never lose work.
          </TipBox>
        </Box>
      ),
    },
    {
      id: 'attachments',
      title: 'Working with Attachments',
      icon: <AttachIcon />,
      content: (
        <Box>
          <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.7 }}>
            Attach files to your emails with ease. CruvzMail supports documents, images, archives,
            and more — with inline image previews and one-click downloads.
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Attachment limits:</Typography>
          <List dense disablePadding>
            {[
              'Maximum file size: 25 MB per attachment',
              'Up to 10 files per email',
              'Total attachment size: 25 MB per message',
              'Supported: PDF, DOCX, XLSX, images, ZIP, and more',
            ].map((item, i) => (
              <ListItem key={i} sx={{ py: 0.25, pl: 0 }}>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <ArrowIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                </ListItemIcon>
                <ListItemText primary={item} primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItem>
            ))}
          </List>
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>Downloading attachments:</Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
            When viewing an email with attachments, you'll see them listed below the message body.
            Click the download icon on any attachment to save it, or use the &quot;Download All&quot; option
            for multiple files.
          </Typography>
          <TipBox>
            Image attachments display inline previews. Click to view full-size before downloading.
          </TipBox>
        </Box>
      ),
    },
    {
      id: 'folders-labels',
      title: 'Folders, Labels & Organization',
      icon: <FolderIcon />,
      content: (
        <Box>
          <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.7 }}>
            Keep your mailbox organized using folders and labels. Move emails into custom folders,
            apply color-coded labels, and use the star feature to highlight important messages.
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Default system folders:</Typography>
          <List dense disablePadding>
            {[
              'Inbox — All incoming messages land here',
              'Sent — Copies of emails you\'ve sent',
              'Drafts — Unfinished messages saved for later',
              'Spam — Messages flagged as unwanted',
              'Trash — Deleted emails (auto-purged after 30 days)',
              'Archive — Emails removed from inbox but preserved',
            ].map((item, i) => (
              <ListItem key={i} sx={{ py: 0.25, pl: 0 }}>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <ArrowIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                </ListItemIcon>
                <ListItemText primary={item} primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItem>
            ))}
          </List>
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>Custom folders & labels:</Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
            Create custom folders from Settings → Folders. Labels can be managed under Settings → Labels.
            Apply labels from the three-dot menu on any email.
          </Typography>
          <TipBox>
            Star important emails by clicking the star icon. Access all starred messages from the
            Starred folder in the sidebar.
          </TipBox>
        </Box>
      ),
    },
    {
      id: 'contacts',
      title: 'Managing Contacts',
      icon: <ContactIcon />,
      content: (
        <Box>
          <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.7 }}>
            CruvzMail includes a built-in contact manager. Save email addresses, organize contacts
            into groups, and enjoy auto-complete suggestions when composing emails.
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Contact features:</Typography>
          <List dense disablePadding>
            {[
              'Add contacts manually or save from received emails',
              'Organize contacts into custom groups',
              'Auto-complete recipients while typing in the To/Cc/Bcc fields',
              'View contact details and email history at a glance',
            ].map((item, i) => (
              <ListItem key={i} sx={{ py: 0.25, pl: 0 }}>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <CheckIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
                </ListItemIcon>
                <ListItemText primary={item} primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItem>
            ))}
          </List>
          <TipBox>
            Access your contacts from the Contacts icon in the sidebar navigation.
          </TipBox>
        </Box>
      ),
    },
    {
      id: 'calendar-tasks',
      title: 'Calendar & Tasks',
      icon: <CalendarIcon />,
      content: (
        <Box>
          <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.7 }}>
            Stay productive with integrated calendar and task management. Schedule meetings directly
            from email conversations and convert messages into actionable tasks.
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Calendar:</Typography>
          <List dense disablePadding>
            {[
              'View daily, weekly, or monthly schedules',
              'Create events with title, time, location, and description',
              'Schedule meetings directly from any email via the three-dot menu',
              'Color-coded events for easy visual scanning',
            ].map((item, i) => (
              <ListItem key={i} sx={{ py: 0.25, pl: 0 }}>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <ArrowIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                </ListItemIcon>
                <ListItemText primary={item} primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItem>
            ))}
          </List>
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>Tasks:</Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
            Convert any email into a task with due dates and priority levels. Manage your task list
            from the Tasks section in the sidebar. Mark tasks complete as you finish them.
          </Typography>
          <TipBox>
            Use &quot;Schedule Meeting&quot; from the email three-dot menu to quickly create a calendar event
            with the email context pre-filled.
          </TipBox>
        </Box>
      ),
    },
    {
      id: 'ai-assistant',
      title: 'AI Assistant',
      icon: <AIIcon />,
      badge: 'New',
      content: (
        <Box>
          <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.7 }}>
            CruvzMail's built-in AI assistant helps you draft, rewrite, and summarize emails. Access
            it from the sidebar for standalone conversations, or use it within the compose editor.
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>What AI can do:</Typography>
          <List dense disablePadding>
            {[
              'Draft professional email responses from a brief prompt',
              'Summarize long email threads into key points',
              'Rewrite your draft in a different tone (formal, casual, concise)',
              'Generate subject lines based on your email content',
              'Translate emails between languages',
            ].map((item, i) => (
              <ListItem key={i} sx={{ py: 0.25, pl: 0 }}>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <CheckIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
                </ListItemIcon>
                <ListItemText primary={item} primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItem>
            ))}
          </List>
          <TipBox>
            Open the AI Assistant from the sidebar and type a natural language request like
            &quot;Write a follow-up email about the project deadline.&quot;
          </TipBox>
        </Box>
      ),
    },
    {
      id: 'search',
      title: 'Searching Your Mailbox',
      icon: <SearchIcon />,
      content: (
        <Box>
          <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.7 }}>
            Quickly find any email using the search bar at the top of the page. Search across
            subject lines, sender names, email addresses, and message content.
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Search tips:</Typography>
          <List dense disablePadding>
            {[
              'Type a keyword or phrase to search across all fields',
              'Search narrows results in real-time as you type',
              'Results show matching emails with highlighted terms',
              'Clear the search to return to your full inbox view',
            ].map((item, i) => (
              <ListItem key={i} sx={{ py: 0.25, pl: 0 }}>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <ArrowIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                </ListItemIcon>
                <ListItemText primary={item} primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItem>
            ))}
          </List>
          <TipBox>
            Use the search bar shortcut — click the search icon or press &apos;/&apos; to focus the
            search field instantly.
          </TipBox>
        </Box>
      ),
    },
    {
      id: 'signatures',
      title: 'Email Signatures',
      icon: <SettingsIcon />,
      content: (
        <Box>
          <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.7 }}>
            Create professional email signatures that are automatically appended to your outgoing
            messages. Use the rich editor to add formatted text, images, and links.
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Setting up your signature:</Typography>
          <List dense disablePadding>
            <StepItem step={1} text="Go to Settings → Signature from the sidebar" />
            <StepItem step={2} text="Use the visual editor to design your signature" />
            <StepItem step={3} text="Add your name, title, phone number, and company info" />
            <StepItem step={4} text="Optionally add images or your company logo using the draw tool" />
            <StepItem step={5} text="Save — your signature will appear in all new emails" />
          </List>
          <TipBox>
            Your signature is placed above any quoted content when replying, following standard
            email conventions.
          </TipBox>
        </Box>
      ),
    },
    {
      id: 'security',
      title: 'Security & Privacy',
      icon: <SecurityIcon />,
      content: (
        <Box>
          <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.7 }}>
            CruvzMail is designed with security at its core. Your data is encrypted, sessions are
            protected, and you have full control over your email infrastructure.
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Security features:</Typography>
          <List dense disablePadding>
            {[
              'TLS encryption for all email transmission (inbound and outbound)',
              'DKIM signing to verify message authenticity',
              'SPF and DMARC policies to prevent spoofing',
              'Secure session management with JWT token rotation',
              'Self-hosted infrastructure — your data never leaves your servers',
              'Spam and phishing detection with automatic filtering',
            ].map((item, i) => (
              <ListItem key={i} sx={{ py: 0.25, pl: 0 }}>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <CheckIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
                </ListItemIcon>
                <ListItemText primary={item} primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItem>
            ))}
          </List>
          <TipBox>
            Check the delivery status panel on sent emails to verify TLS encryption was used for
            each recipient.
          </TipBox>
        </Box>
      ),
    },
    {
      id: 'user-management',
      title: 'User Onboarding & Management',
      icon: <GroupIcon />,
      content: (
        <Box>
          <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.7 }}>
            Administrators can manage users, assign roles, and control access through the admin panel.
            New users can be added individually or in bulk during the setup process.
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>For administrators:</Typography>
          <List dense disablePadding>
            {[
              'Add new users with email address, name, and initial password',
              'Assign roles: Admin, Manager, or User',
              'View and manage all organization users from the Admin Panel',
              'Reset passwords and manage account status',
              'Configure domain-wide settings and branding',
            ].map((item, i) => (
              <ListItem key={i} sx={{ py: 0.25, pl: 0 }}>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <ArrowIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                </ListItemIcon>
                <ListItemText primary={item} primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItem>
            ))}
          </List>
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>For new users:</Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
            When you receive your login credentials from your administrator, sign in and explore the
            platform. Start by setting up your signature, customizing your theme in Settings → System,
            and familiarizing yourself with the sidebar navigation.
          </Typography>
        </Box>
      ),
    },
    {
      id: 'delivery-tracking',
      title: 'Email Delivery Tracking',
      icon: <TaskIcon />,
      badge: 'New',
      content: (
        <Box>
          <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.7 }}>
            Monitor how your sent emails are delivered with CruvzMail's built-in delivery tracking.
            View real-time delivery status, SMTP responses, and encryption details for every message
            you send.
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>What you can track:</Typography>
          <List dense disablePadding>
            {[
              'Delivery status: Delivered, Pending, or Failed',
              'SMTP server response codes and messages',
              'TLS encryption verification for each recipient',
              'Message size and transmission details',
              'Timestamped delivery timeline',
            ].map((item, i) => (
              <ListItem key={i} sx={{ py: 0.25, pl: 0 }}>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <CheckIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
                </ListItemIcon>
                <ListItemText primary={item} primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItem>
            ))}
          </List>
          <TipBox>
            Open any sent email and look for the delivery status panel below the message to see
            detailed tracking information.
          </TipBox>
        </Box>
      ),
    },
    {
      id: 'keyboard-shortcuts',
      title: 'Keyboard Shortcuts',
      icon: <KeyboardIcon />,
      content: (
        <Box>
          <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.7 }}>
            Speed up your workflow with keyboard shortcuts. These work anywhere in the application
            when you're not focused on a text input field.
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
            {[
              { key: 'C', action: 'New compose' },
              { key: '/', action: 'Focus search' },
              { key: 'R', action: 'Reply' },
              { key: 'A', action: 'Reply all' },
              { key: 'F', action: 'Forward' },
              { key: 'E', action: 'Archive' },
              { key: '#', action: 'Delete' },
              { key: 'S', action: 'Toggle star' },
              { key: 'U', action: 'Mark unread' },
              { key: 'Esc', action: 'Close / Go back' },
            ].map((shortcut, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  component="kbd"
                  sx={{
                    px: 1, py: 0.25, borderRadius: 0.5, fontSize: '0.8rem', fontWeight: 600,
                    fontFamily: 'monospace', minWidth: 32, textAlign: 'center',
                    bgcolor: isDark ? alpha('#fff', 0.08) : alpha('#000', 0.06),
                    border: `1px solid ${isDark ? alpha('#fff', 0.15) : alpha('#000', 0.12)}`,
                    color: theme.palette.text.primary,
                  }}
                >
                  {shortcut.key}
                </Box>
                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                  {shortcut.action}
                </Typography>
              </Box>
            ))}
          </Box>
          <TipBox>
            Keyboard shortcuts can be customized from Settings → Hotkeys.
          </TipBox>
        </Box>
      ),
    },
    {
      id: 'mobile-access',
      title: 'Mobile & Multi-Device Access',
      icon: <MobileIcon />,
      content: (
        <Box>
          <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.7 }}>
            Access CruvzMail from any device with a web browser. The interface is fully responsive
            and adapts to phone, tablet, and desktop screen sizes.
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Access options:</Typography>
          <List dense disablePadding>
            {[
              'Web browser on any device — no app installation required',
              'Responsive design optimized for mobile screens',
              'IMAP/SMTP support for third-party email clients',
              'Configure on Outlook, Thunderbird, Apple Mail, or any IMAP client',
              'Desktop notifications for new email alerts (browser-based)',
            ].map((item, i) => (
              <ListItem key={i} sx={{ py: 0.25, pl: 0 }}>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <ArrowIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                </ListItemIcon>
                <ListItemText primary={item} primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItem>
            ))}
          </List>
          <TipBox>
            For the best experience on mobile, add CruvzMail to your home screen using your
            browser's &quot;Add to Home Screen&quot; option.
          </TipBox>
        </Box>
      ),
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting & Support',
      icon: <SettingsIcon />,
      content: (
        <Box>
          <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.7 }}>
            Encountering issues? Here are common solutions and how to get help.
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Common issues:</Typography>
          <List dense disablePadding>
            {[
              { q: 'Can\'t log in?', a: 'Verify your email and password. Use "Forgot Password" if needed, or contact your admin.' },
              { q: 'Emails going to spam?', a: 'Ask your admin to verify SPF, DKIM, and DMARC records for your domain.' },
              { q: 'Attachments failing?', a: 'Check file size (max 25 MB per file, 25 MB total). Ensure the file type is supported.' },
              { q: 'Not receiving emails?', a: 'Check your spam folder. Verify MX records are pointing to your mail server.' },
              { q: 'Slow performance?', a: 'Try clearing your browser cache. Ensure you\'re on a stable internet connection.' },
            ].map((item, i) => (
              <ListItem key={i} sx={{ py: 0.5, pl: 0, alignItems: 'flex-start' }}>
                <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}>
                  <ArrowIcon sx={{ fontSize: 16, color: theme.palette.warning.main }} />
                </ListItemIcon>
                <ListItemText
                  primary={item.q}
                  secondary={item.a}
                  primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600 }}
                  secondaryTypographyProps={{ fontSize: '0.825rem' }}
                />
              </ListItem>
            ))}
          </List>
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>Need more help?</Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
            Contact your organization's email administrator or reach out to CruvzMail support at{' '}
            <strong>{branding.supportEmail || 'support@cruvzmail.com'}</strong>.
          </Typography>
        </Box>
      ),
    },
  ];

  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'auto',
        bgcolor: theme.palette.background.default,
        p: { xs: 2, sm: 3 },
      }}
    >
      <Box sx={{ maxWidth: 820, mx: 'auto' }}>
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            mb: 3,
            borderRadius: 2,
            bgcolor: isDark ? alpha(theme.palette.primary.main, 0.08) : alpha(theme.palette.primary.main, 0.04),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            {branding.logo ? (
              <Box
                component="img"
                src={branding.logo}
                alt={branding.companyName}
                sx={{ height: 40, objectFit: 'contain' }}
              />
            ) : (
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, color: theme.palette.primary.main, letterSpacing: '-0.02em' }}
              >
                {branding.companyName || 'CruvzMail'}
              </Typography>
            )}
          </Box>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, mb: 1, color: theme.palette.text.primary, letterSpacing: '-0.01em' }}
          >
            Getting Started Guide
          </Typography>
          <Typography variant="body1" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
            Everything you need to know to make the most of your {branding.companyName || 'CruvzMail'} experience.
            From sending your first email to advanced features — we've got you covered.
          </Typography>
        </Paper>

        {/* Sections */}
        {sections.map((section) => (
          <Accordion
            key={section.id}
            expanded={expanded === section.id}
            onChange={handleChange(section.id)}
            elevation={0}
            disableGutters
            sx={{
              mb: 1,
              borderRadius: '8px !important',
              border: `1px solid ${theme.palette.divider}`,
              '&:before': { display: 'none' },
              overflow: 'hidden',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                px: 2.5, py: 0.5,
                '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1.5, my: 1 },
              }}
            >
              <Box sx={{ color: theme.palette.primary.main, display: 'flex', alignItems: 'center' }}>
                {section.icon}
              </Box>
              <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', flex: 1 }}>
                {section.title}
              </Typography>
              {section.badge && (
                <Chip
                  label={section.badge}
                  size="small"
                  color={section.badge === 'New' ? 'success' : 'primary'}
                  sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
                />
              )}
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
              <Divider sx={{ mb: 2 }} />
              {section.content}
            </AccordionDetails>
          </Accordion>
        ))}

        {/* Footer */}
        <Box sx={{ textAlign: 'center', py: 4, color: theme.palette.text.secondary }}>
          <Typography variant="body2" sx={{ fontSize: '0.825rem' }}>
            {branding.copyrightText || `© ${new Date().getFullYear()} CruvzMail. All rights reserved.`}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.775rem', mt: 0.5, opacity: 0.7 }}>
            Questions? Reach out to {branding.supportEmail || 'support@cruvzmail.com'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default GuidePage;
